import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Button,
  Spinner,
} from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { Period } from "modules/Clinical/TimePoints/helpers/timpoints.types";
import { loadCodelists, DEVIATION_REASONS_OPTIONS, CommonOption } from "api/modules/common.option";
import { validations } from "validations/FormValidations";

import { DeviationReasonTarget } from "../helper/manualEntry.types";

interface AddEditDeviationReasonModalProps {
  isOpen: boolean;
  toggle: () => void;
  target: DeviationReasonTarget | null;
  onSave: (reason: string) => void;
  isPending: boolean;
}

interface DeviationFormValues {
  presetReason: string;
  customReason: string;
}

const AddEditDeviationReasonModal: React.FC<AddEditDeviationReasonModalProps> = ({
  isOpen,
  toggle,
  target,
  onSave,
  isPending,
}) => {
  const [deviationReasonOptions, setDeviationReasonOptions] = React.useState<CommonOption[]>([]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm<DeviationFormValues>({
    defaultValues: {
      presetReason: "",
      customReason: "",
    },
  });

  const watchedPresetReason = watch("presetReason") || "";
  const watchedCustomReason = watch("customReason") || "";
  const isCustomSelected = watchedPresetReason === "Customized / Other";

  // Fetch Codelist Options for Deviation Reasons
  useEffect(() => {
    loadCodelists(["DEVIATION_REASONS_OPTIONS"]).then(() => {
      if (DEVIATION_REASONS_OPTIONS && DEVIATION_REASONS_OPTIONS.length > 0) {
        setDeviationReasonOptions([...DEVIATION_REASONS_OPTIONS]);
      }
    });
  }, []);

  useEffect(() => {
    if (isOpen && target) {
      const existingReason = target.currentReason || "";
      const isPreset = deviationReasonOptions.some((opt) => opt.label === existingReason);

      let presetVal = "";
      let customVal = "";
      if (isPreset) {
        presetVal = existingReason;
        customVal = "";
      } else if (existingReason) {
        presetVal = "Customized / Other";
        customVal = existingReason;
      }

      reset({
        presetReason: presetVal,
        customReason: customVal,
      });
    }
  }, [isOpen, target, deviationReasonOptions, reset]);

  if (!target) return null;

  const selectOptions = [
    ...deviationReasonOptions.map((opt) => ({
      label: opt.label,
      value: opt.label,
    })),
    { label: "Customized / Other", value: "Customized / Other" },
  ];

  const formFields: FormFieldConfig[] = [
    {
      name: "presetReason",
      label: "Select Standard Reason",
      type: "select",
      required: true,
      placeholder: "-- Select Preset Reason --",
      options: selectOptions,
      rules: validations.subjectPeriod.presetReason,
      onChangeExtra: (selectedOption: any) => {
        const val = selectedOption ? String(selectedOption.value) : "";
        if (val !== "Customized / Other") {
          setValue("customReason", "", { shouldValidate: true });
        }
      },
    },
    ...(isCustomSelected
      ? [
          {
            name: "customReason",
            label: "Customized Reason / Explanation",
            type: "textarea",
            required: true,
            placeholder: "Provide detailed explanation for this deviation (max 250 characters)...",
            maxLength: 250,
            rules: validations.subjectPeriod.deviationReason,
          } as FormFieldConfig,
        ]
      : []),
  ];

  const onSubmit = (data: DeviationFormValues) => {
    let finalReason = "";
    if (data.presetReason === "Customized / Other") {
      finalReason = data.customReason.trim();
    } else {
      finalReason = data.presetReason.trim();
    }

    if (!finalReason) return;
    if (data.presetReason === "Customized / Other" && finalReason.length > 250) return;

    onSave(finalReason);
  };

  const isSaveDisabled =
    !watchedPresetReason ||
    (isCustomSelected && (!watchedCustomReason.trim() || watchedCustomReason.length > 250)) ||
    isPending;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="bg-light border-bottom p-3">
        <div className="d-flex align-items-center gap-2">
          <div className="avatar-xs flex-shrink-0">
            <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-15">
              <i className="ri-alert-line"></i>
            </span>
          </div>
          <div>
            <h6 className="mb-0 fw-bold fs-14">
              {target.currentReason ? "Edit Deviation Reason" : "Add Deviation Reason"}
            </h6>
            <p className="text-muted fs-11 mb-0">
              Provide clinical rationale for sample collection time deviation
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-3">
        <div className="bg-light p-3 rounded-3 border mb-3">
          <Row className="g-2 text-dark fs-12 mb-2">
            <Col xs={4}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Period</span>
              <strong className="fs-12">
                {target.period.period_name ||
                  target.period.periodname ||
                  `Period ${target.period.period_number}`}
              </strong>
            </Col>
            <Col xs={4}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Time Point</span>
              <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-0.5 fw-bold fs-11">
                {target.row.timepoint}
              </span>
            </Col>
            <Col xs={4}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Deviation Status</span>
              <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-2 py-0.5 fw-bold fs-11">
                {target.row.deviationstatus || "Deviation"}
              </span>
            </Col>
          </Row>
          <Row className="g-2 text-dark fs-12 border-top pt-2">
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Scheduled Dose Time</span>
              <span className="text-muted fw-medium">{target.row.actualTime || "--"}</span>
            </Col>
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Collection Time</span>
              <span className="text-dark fw-bold">{target.row.samplecollectiontime || "--"}</span>
            </Col>
          </Row>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <CommonForm
            fields={formFields}
            control={control}
            errors={errors}
            columns={1}
            setValue={setValue}
            getValues={getValues}
          />
          {isCustomSelected && (
            <div className="d-flex justify-content-end mt-1 mb-2">
              <span
                className={`fs-11 ${
                  watchedCustomReason.length > 250 ? "text-danger fw-bold" : "text-muted"
                }`}
              >
                {250 - watchedCustomReason.length} characters left
              </span>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 border-top pt-3">
            <Button color="light" className="px-3 btn-sm border" onClick={toggle} disabled={isPending}>
              Cancel
            </Button>
            <Button
              color="primary"
              className="px-3 btn-sm d-flex align-items-center gap-1"
              disabled={isSaveDisabled}
              type="submit"
            >
              {isPending ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="ri-save-line fs-14"></i>
                  Save Reason
                </>
              )}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default React.memo(AddEditDeviationReasonModal);
